import { CANCEL_KEYS } from '@adsk/docs-http-client';
import { isBrowser, isNode, isFile, isFilePath, isDataURIScheme } from './common';
import { ERROR, UPLOAD_STATUS, ossCancelTokenMap } from './consts';
import { getDataURISchemeChunk, getFileBlob, readBlob, readFileChunk, getToCompleteFile, getWaitingForUpload } from './file';
export const getAndPutFilePartTask = ({
  queue,
  httpService,
  onUpdateStatus
}) => {
  const [part, file] = getWaitingForUpload(queue);
  if (!part) {
    return null;
  }
  onUpdateStatus({
    id: part.id,
    fileId: part.fileId,
    status: UPLOAD_STATUS.UPLOAD_CONTENT
  });
  const cancelRequestConfig = {
    getter: () => ossCancelTokenMap.get(part.id),
    setter: tokenId => ossCancelTokenMap.set(part.id, tokenId),
    clearer: () => ossCancelTokenMap.delete(part.id)
  };
  const task = async () => {
    const payload = {
      bucketKey: file.bucketKey,
      objectKey: file.objectKey,
      firstPart: part.part
    };
    if (file.uploadKey) {
      payload.uploadKey = file.uploadKey;
    }
    const options = Object.assign({
      [CANCEL_KEYS]: cancelRequestConfig
    }, file.options);
    try {
      const {
        data: {
          uploadExpiration,
          uploadKey,
          urlExpiration,
          urls
        }
      } = await httpService.getSignedUrl(payload, options);
      const url = urls[0]; // only one url in urls

      onUpdateStatus({
        id: part.id,
        fileId: part.fileId,
        url,
        urlExpiration,
        uploadExpiration,
        uploadKey
      });
      const handleProgress = progress => {
        onUpdateStatus({
          id: part.id,
          fileId: part.fileId,
          progress
        });
      };
      let chunk;
      if (isBrowser()) {
        if (isDataURIScheme(file.ref)) {
          chunk = getDataURISchemeChunk(file.ref, part.part, file.fileChunkSize);
        } else {
          const blob = getFileBlob(file.ref, part.part, file.fileChunkSize);
          chunk = await readBlob(blob);
        }
      } else if (isNode()) {
        if (isFile(file.ref)) {
          chunk = getFileBlob(file.ref, part.part, file.fileChunkSize);
        } else if (isFilePath(file.ref)) {
          chunk = await readFileChunk(file.ref, part.part, file.fileChunkSize);
        }
      }
      if (!chunk) {
        throw {
          code: ERROR.FILE_INVALID,
          detail: file.ref
        };
      }
      await httpService.putToSignedUrl(url, chunk, handleProgress, {
        [CANCEL_KEYS]: cancelRequestConfig
      });
      onUpdateStatus({
        id: part.id,
        fileId: part.fileId,
        status: UPLOAD_STATUS.UPLOAD_CONTENT_SUCCESS
      });
    } catch (error) {
      onUpdateStatus({
        id: part.id,
        fileId: part.fileId,
        status: UPLOAD_STATUS.UPLOAD_CONTENT_FAILED,
        error
      });
    }
  };
  return task;
};
export const getUploadCompleteTask = ({
  queue,
  httpService,
  onUpdateStatus
}) => {
  const file = getToCompleteFile(queue);
  if (!file) return undefined;
  onUpdateStatus({
    id: file.id,
    status: UPLOAD_STATUS.COMPLETE_UPLOAD_PENDING
  });
  const payload = {
    bucketKey: file.bucketKey,
    objectKey: file.objectKey,
    uploadKey: file.uploadKey,
    size: file.size
  };
  const options = Object.assign({
    [CANCEL_KEYS]: {
      getter: () => ossCancelTokenMap.get(file.id),
      setter: tokenId => ossCancelTokenMap.set(file.id, tokenId),
      clearer: () => ossCancelTokenMap.delete(file.id)
    }
  }, file.options);
  const task = async () => {
    try {
      const {
        data
      } = await httpService.completeUpload(payload, options);
      onUpdateStatus({
        id: file.id,
        status: UPLOAD_STATUS.COMPLETE_UPLOAD_SUCCESS,
        res: data
      });
    } catch (error) {
      onUpdateStatus({
        id: file.id,
        status: UPLOAD_STATUS.COMPLETE_UPLOAD_FAILED,
        error
      });
    }
  };
  return task;
};

/**
 * task priority
 * 1. upload complete task
 * 2. file part task
 *
 */
export const getWorkerTask = ({
  queue,
  httpService,
  onUpdateStatus
}) => {
  const uploadCompleteTask = getUploadCompleteTask({
    queue,
    httpService,
    onUpdateStatus
  });
  if (uploadCompleteTask) {
    return uploadCompleteTask;
  }
  const filePartTask = getAndPutFilePartTask({
    queue,
    httpService,
    onUpdateStatus
  });
  if (filePartTask) {
    return filePartTask;
  }
  return undefined;
};